import { useEffect } from 'react'
import { GetServerSideProps } from 'next'
import qs from 'qs'
import { googleMapLibraries } from 'constants/common'
import withGoogleMapsScript from 'hoc/withGoogleMapsScript'
import { getFilters } from 'dataSource/ProviderSearch'
import { getFilterLookup, groupFilters } from 'app/ProviderSearch/utils'
import SearchPage from 'app/ProviderSearch/Pages/SearchPage'
import { buildProviderSearchCanonicalLink } from 'app/SeFriendly/canonicalLink'
import { handleCaptureRwgToken } from 'utils/rwgConversionTracking'
import { DeviceProvider } from 'context/common/DeviceContext'
import InsuranceProvider from 'context/common/InsuranceProvider'
import { parseDeviceTypeHeader } from 'utils/deviceTypeFromUserAgent'
import { getRequestOrigin } from 'utils/getRequestOrigin'

const ProvidersListingPage: React.FC<ProvidersListingPageProps> = ({
  groupedFilters,
  filterLookup,
  deviceType,
  canonicalLink,
}) => {
  // Capture RwG token from URL if available
  useEffect(handleCaptureRwgToken, [])

  return (
    <DeviceProvider deviceType={deviceType}>
      <InsuranceProvider>
        <SearchPage
          groupedFilters={groupedFilters}
          filterLookup={filterLookup}
          canonicalLink={canonicalLink}
        />
      </InsuranceProvider>
    </DeviceProvider>
  )
}

export const getServerSideProps: GetServerSideProps<ProvidersListingPageProps> = async (
  ctx
) => {
  const response = await getFilters()
  const groupedFilters = groupFilters(response.result)
  const filterLookup = getFilterLookup(response.result)

  const deviceType = parseDeviceTypeHeader(ctx.req.headers['x-device-type'])

  // The canonical tag is nice-to-have SEO metadata, so a malformed query string
  // must never cost us the whole page. Falling through to '' reproduces the
  // pre-render state of the hook this replaced.
  let canonicalLink = ''

  try {
    canonicalLink = buildProviderSearchCanonicalLink({
      query: qs.parse(ctx.resolvedUrl.split('?')[1] ?? ''),
      groupedFilters,
      origin: getRequestOrigin(ctx.req),
    })
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to build provider-search canonical link', error)
  }

  return {
    props: { groupedFilters, filterLookup, deviceType, canonicalLink },
  }
}

export default withGoogleMapsScript(ProvidersListingPage, [
  googleMapLibraries.GEOMETRY,
  googleMapLibraries.DRAWING,
  googleMapLibraries.PLACES,
])
